import React, { useState, useEffect } from 'react';

interface AvatarProps {
  src?: string;
  alt: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const Avatar: React.FC<AvatarProps> = ({ 
  src, 
  alt, 
  className = '', 
  size = 'md'
}) => {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [hasError, setHasError] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  const sizeClasses = {
    sm: 'w25 h25',
    md: 'w40 h40', 
    lg: 'w60 h60'
  };

  const fallbackSrc = "assets/images/user.png";

  // Update currentSrc when src prop changes
  useEffect(() => {
    setCurrentSrc(src);
    setHasError(false);
    setShowFallback(false);
  }, [src]);

  const handleError = () => {
    console.warn(`Failed to load avatar: ${currentSrc}`);
    
    if (!hasError && currentSrc !== fallbackSrc) {
      console.log(`Using fallback avatar: ${fallbackSrc}`);
      setCurrentSrc(fallbackSrc);
      setHasError(true);
    } else {
      console.error(`Avatar fallback also failed for: ${alt}, showing CSS placeholder`);
      setShowFallback(true);
    }
  };

  const handleLoad = () => {
    setHasError(false);
    setShowFallback(false);
  };

  // Determine the source to use
  const getImageSrc = () => {
    // If no src provided or empty string, use fallback immediately
    if (!src || src.trim() === '') {
      return fallbackSrc;
    }
    // Otherwise use currentSrc (which may be fallback after error)
    return currentSrc || fallbackSrc;
  };

  // Get initials from alt text for CSS fallback
  const getInitials = () => {
    return alt
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // If we should show CSS fallback
  if (showFallback) {
    return (
      <div
        className={`${sizeClasses[size]} rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-600 ${className}`}
        style={{
          fontSize: size === 'sm' ? '10px' : size === 'lg' ? '20px' : '14px',
          backgroundColor: '#6c757d'
        }}
      >
        {getInitials()}
      </div>
    );
  }

  return (
    <img
      src={getImageSrc()}
      alt={alt}
      className={`${sizeClasses[size]} rounded-circle ${className}`}
      onError={handleError}
      onLoad={handleLoad}
    />
  );
};

export default Avatar;